import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import LockIcon from "@mui/icons-material/Lock"
import LockOpenIcon from "@mui/icons-material/LockOpen"
import VisibilityRoundedIcon from "@mui/icons-material/Visibility"
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOff"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { useTheme } from "@mui/material/styles"
import { Box } from "@mui/system"
import { t } from "i18next"
import { memo } from "react"
import { shallowEqual } from "react-redux"
import type { AnySceneObjectT } from "tweenly"
import { getObjectDataSourceContent } from "tweenly"
import { selectUndoable } from "../../../helpers/selector.helpers"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useObjectsTreeRowDrag } from "../../../hooks/useObjectsTreeRowDrag"
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux"
import {
    selectObjectsByIds,
    updateObjectVisibilityStylesAction,
} from "../../../store/slices/objects.slice"
import DataSourceIndicator from "../DataSource/DataSourceIndicator"
import ObjectAnimationSelection from "./ObjectAnimationSelection"
import ObjectsTreeNodeMenu from "./ObjectsTreeNodeMenu"

interface Props {
    object: AnySceneObjectT
    numberOfObjects: number
    isHiding: boolean
    isLocking: boolean
    isExpanded: boolean
}

const ObjectsTreeNode = ({ object, numberOfObjects, isHiding, isLocking, isExpanded }: Props) => {
    const dispatch = useAppDispatch()
    // only this node's direct children, compared shallowly — a change to any other object
    // leaves these references untouched, so the rest of the tree bails out instead of
    // re-rendering (this fired on every mouse-up at the end of a drag)
    const childObjects = useAppSelector(
        (state) => selectObjectsByIds(selectUndoable(state).objects, object.childIds),
        shallowEqual
    )

    const theme = useTheme()

    const isVisible = !object.isHidden
    const isUnlockLocked = !object.isLocked
    const isCollapsed = object.type === "ticker" ? false : !object.isExpanded
    const { updateObject } = useObjectActions()

    const handleVisibility = () => {
        dispatch(updateObjectVisibilityStylesAction({ object, isHidden: !object.isHidden }))
    }
    const handleLock = () => {
        updateObject(object.id, "isLocked", !object.isLocked)
    }

    const handleExpand = () => {
        updateObject(object.id, "isExpanded", !object.isExpanded)
    }

    // drag & drop to rearrange — a locked object (or one inside a locked one) stays put
    const { placement, isCarried, rowProps } = useObjectsTreeRowDrag(
        object,
        object.isLocked || isLocking
    )

    const renderChild = (childId: string, childIndex: number) => {
        const childObject = childObjects[childIndex]
        if (!childObject) return null

        // A collapsed branch renders nothing at all. It used to stay in the DOM behind
        // `display: none`, and every node carries three IconButtons with Tooltips plus the
        // whole ObjectsTreeNodeMenu — some 20-30 DOM nodes each, for objects nobody can see.
        // Those nodes also cost a full style recalculation of the document on every change.
        if (childObject.parentId === object?.id && (!isExpanded || isCollapsed)) return null

        return (
            <Box key={childObject.id}>
                <ObjectsTreeNode
                    object={childObject}
                    numberOfObjects={object.childIds.length}
                    isHiding={isHiding || !isVisible}
                    isLocking={isLocking || !isUnlockLocked}
                    isExpanded={isExpanded || !isCollapsed}
                />
            </Box>
        )
    }
    if (!object) return <></>

    return (
        <Box
            sx={{
                paddingLeft: 2,
                width: "100%",
                // being dragged: it stays where it is, dimmed, so it is clear what is moving
                ...(isCarried && { opacity: 0.5 }),
            }}
        >
            <Grid
                container
                direction="row"
                alignItems="center"
                wrap="nowrap"
                {...rowProps}
                sx={{
                    position: "relative",
                    marginBottom: 1,
                    padding: 1,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                    // where the dragged objects would land: inside this row — or in the gap
                    // above / below it, as its sibling
                    boxShadow:
                        placement === "inside"
                            ? `inset 0 0 0 1px ${theme.palette.primary.main}`
                            : undefined,
                    "&::after":
                        placement === "before" || placement === "after"
                            ? {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  right: 0,
                                  height: 2,
                                  borderRadius: 1,
                                  backgroundColor: theme.palette.primary.main,
                                  pointerEvents: "none",
                                  [placement === "before" ? "top" : "bottom"]: "-5px",
                              }
                            : undefined,
                }}
            >
                <Grid item id="editor-step-3">
                    {!isCollapsed ? (
                        <Tooltip title={!object.childIds.length ? "" : t("editor:collapse")}>
                            <span>
                                <IconButton
                                    onClick={handleExpand}
                                    disabled={
                                        object.type === "ticker"
                                            ? true
                                            : object.childIds.length === 0
                                            ? true
                                            : false
                                    }
                                >
                                    <KeyboardArrowDownIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={t("editor:expand")}>
                            <IconButton onClick={handleExpand}>
                                <KeyboardArrowRightIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Grid>
                <Grid item sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
                    <ObjectsTreeNodeMenu object={object} numberOfObjects={numberOfObjects} />
                </Grid>
                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                    <DataSourceIndicator content={getObjectDataSourceContent(object)} />
                </Grid>
                <Grid item>
                    <Tooltip title={object.isLocked ? t("editor:unlock") : t("editor:lock")}>
                        <span>
                            <IconButton onClick={handleLock} disabled={isLocking}>
                                {isUnlockLocked && !isLocking ? (
                                    <LockOpenIcon fontSize="small" />
                                ) : (
                                    <LockIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title={isVisible ? t("editor:hide") : t("editor:show")}>
                        <span>
                            <IconButton onClick={handleVisibility} disabled={isHiding}>
                                {isVisible && !isHiding ? (
                                    <VisibilityRoundedIcon fontSize="small" />
                                ) : (
                                    <VisibilityOffRoundedIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <ObjectAnimationSelection object={object} invisible={!isVisible || isHiding} />
                </Grid>
            </Grid>
            {object.type !== "ticker" && object.childIds.map(renderChild)}
        </Box>
    )
}

/**
 * memo: the tree re-renders whenever the selection or any object changes, but every prop here
 * is referentially stable, so untouched branches can bail out.
 */
export default memo(ObjectsTreeNode)
